import { useMemo } from 'react';

export interface MustWinSwimLane {
  audienceId: string;
  audienceName: string;
  strategyId: string;
  strategyName: string;
  type: string;
  projects: Array<{ quarter: string; type: string; project: any[] }>;
}

export interface GroupedByAudience {
  audienceName: string;
  strategies: MustWinSwimLane[];
}

/**
 * Builds swimlanes from L1/L2/L3 releases with audienceStrategyMappings,
 * and groups them by audience for Must Wins view rendering.
 * No mustWin check — all cards with audienceStrategyMappings are included.
 * Supports both payload formats: object { "Audience": ["Strategy", ...] } and legacy array.
 */
export function useMustWinSwimLanes(releases: any): {
  mustWinSwimLanes: MustWinSwimLane[];
  groupedByAudience: GroupedByAudience[];
} {
  const mustWinSwimLanes = useMemo(() => {
    const audienceStrategyMap: Record<string, Record<string, any>> = {};

    const allReleases = [...(releases?.releaseScheduled || [])];

    allReleases.forEach((releaseType: any) => {

      if (
        releaseType.type === 'L1' ||
        releaseType.type === 'L2' ||
        releaseType.type === 'L3' ||
        releaseType.type === 'Price Changes and Offers'
      ) {
        releaseType.projects?.forEach((quarterProject: any) => {
          quarterProject.project?.forEach((card: any) => {
            if (card.audienceStrategyMappings) {
              const mappings = card.audienceStrategyMappings;
              const isNewFormat =
                !Array.isArray(mappings) &&
                typeof mappings === 'object' &&
                mappings !== null;

              const addCardToSwimLane = (
                audienceName: string,
                strategyName: string,
                audienceId?: string,
                strategyId?: string
              ) => {
                const audienceKey = (audienceId || audienceName).trim();
                const strategyKey = (strategyId || strategyName).trim();
                const swimLaneKey = `${audienceKey}::${strategyKey}`;

                if (!audienceStrategyMap[swimLaneKey]) {
                  audienceStrategyMap[swimLaneKey] = {
                    audienceId:
                      audienceId ||
                      audienceName.replace(/\s+/g, '-').toLowerCase(),
                    audienceName: audienceName.trim(),
                    strategyId:
                      strategyId ||
                      strategyName.replace(/\s+/g, '-').toLowerCase(),
                    strategyName: strategyName.trim(),
                    type: swimLaneKey,
                    projectsByQuarter: {}
                  };
                }

                const quarter = quarterProject.quarter;
                if (
                  !audienceStrategyMap[swimLaneKey].projectsByQuarter[quarter]
                ) {
                  audienceStrategyMap[swimLaneKey].projectsByQuarter[quarter] =
                    [];
                }
                audienceStrategyMap[swimLaneKey].projectsByQuarter[
                  quarter
                ].push(card);
              };

              if (isNewFormat) {
                Object.entries(mappings as Record<string, string[]>).forEach(
                  ([audienceName, strategyNames]) => {
                    const list = Array.isArray(strategyNames)
                      ? strategyNames
                      : [];
                    list.forEach((strategyName: string) => {
                      addCardToSwimLane(audienceName, strategyName);
                    });
                  }
                );
              } else {
                (mappings as any[]).forEach((mapping: any) => {
                  addCardToSwimLane(
                    mapping.audienceName,
                    mapping.strategyName,
                    mapping.audienceId,
                    mapping.strategyId
                  );
                });
              }
            }
          });
        });
      }
    });

    return Object.values(audienceStrategyMap).map((swimLane: any) => {
      const projects = Object.entries(swimLane.projectsByQuarter).map(
        ([quarter, cards]) => ({
          quarter,
          type: swimLane.type,
          project: cards
        })
      );

      return {
        audienceId: swimLane.audienceId,
        audienceName: swimLane.audienceName,
        strategyId: swimLane.strategyId,
        strategyName: swimLane.strategyName,
        type: swimLane.type,
        projects: projects as any[]
      };
    });
  }, [releases]);

  const groupedByAudience = useMemo(() => {
    const grouped: Record<string, MustWinSwimLane[]> = {};

    mustWinSwimLanes.forEach(swimLane => {
      const audienceName = swimLane.audienceName;
      if (!grouped[audienceName]) {
        grouped[audienceName] = [];
      }
      grouped[audienceName].push(swimLane);
    });

    return Object.entries(grouped).map(([audienceName, strategies]) => ({
      audienceName,
      strategies
    }));
  }, [mustWinSwimLanes]);

  return { mustWinSwimLanes, groupedByAudience };
}
